/**
 * CascadingMenu — menu cascata onde cada grupo abre um painel lateral (à direita).
 * Abre no hover (mouse) E no clique/toque (tablet), por isso usa Popover controlado
 * em vez de HoverCard (que só responde a hover).
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Bullet padrão para itens folha em níveis profundos
function Bullet({ active }) {
  return (
    <span
      className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
        active ? "bg-[color:var(--accent)]" : "bg-gray-400"
      }`}
      aria-hidden
    />
  );
}

function MenuLeaf({ node, depth, location, onClose, extLinkProps, badges = {} }) {
  const Icon = node.icon;
  const active = location.pathname === node.path;
  const useBullet = depth >= 2;
  const tag = node.desativado ? (
    <span
      className="inline-flex items-center justify-center h-4 px-1 rounded-full text-[10px] font-semibold text-white bg-[#111827]"
      data-testid={`sidebar-node-tag-${node.path.split("/").pop()}`}
    >
      {node.tag}
    </span>
  ) : null;
  if (node.desativado) {
    return (
      <div
        aria-disabled="true"
        data-tip="Em breve — recurso em desenvolvimento"
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-gray-600 opacity-60 cursor-not-allowed dark:text-gray-300"
        data-testid={`sidebar-node-${node.path.split("/").pop()}`}
      >
        {useBullet ? <Bullet active={false} /> : <Icon size={14} className="text-gray-400 shrink-0" />}
        <span className="truncate flex-1">{node.label}</span>
        {tag}
      </div>
    );
  }
  return (
    <Link
      to={node.path}
      {...extLinkProps(node)}
      onClick={onClose}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
        active
          ? "bg-gray-100 text-gray-900 font-medium dark:bg-gray-700 dark:text-white"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700"
      }`}
      data-testid={`sidebar-node-${node.path.split("/").pop()}`}
    >
      {useBullet ? <Bullet active={active} /> : <Icon size={14} className="text-gray-400 shrink-0" />}
      <span className="truncate flex-1">{node.label}</span>
      {badges[node.path] > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-semibold text-white bg-[#111827]"
          data-testid={`sidebar-node-badge-${node.path.split("/").pop()}`}
        >
          {badges[node.path]}
        </span>
      )}
    </Link>
  );
}

function MenuGroup({ node, depth, location, onClose, extLinkProps }) {
  const Icon = node.icon;
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const openNow = () => { clearTimeout(timer.current); setOpen(true); };
  const closeSoon = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 180);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          data-testid={`sidebar-group-${node.path.replace(/^\//, "").replace(/\//g, "-")}`}
        >
          <Icon size={14} className="text-gray-400 shrink-0" />
          <span className="truncate flex-1 text-left">{node.label}</span>
          <span className="text-gray-300 text-xs">›</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={4}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="p-1.5 w-auto min-w-[220px] max-w-[280px] max-h-[calc(100vh-80px)] overflow-y-auto bg-white border border-gray-200 shadow-xl rounded-lg z-[80] dark:bg-gray-800 dark:border-gray-700"
      >
        <div className="px-2 py-1 border-b border-gray-100 dark:border-gray-700 mb-1">
          <div className="flex items-center gap-1.5">
            <Icon size={12} className="text-gray-400" />
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {node.label}
            </span>
          </div>
        </div>
        <MenuLevel
          items={node.children}
          depth={depth + 1}
          location={location}
          onClose={onClose}
          extLinkProps={extLinkProps}
        />
      </PopoverContent>
    </Popover>
  );
}

export function MenuLevel({ items, depth = 0, location, onClose, extLinkProps, badges = {} }) {
  return (
    <div className="space-y-0.5">
      {(items || []).map((n) => {
        const hasChildren = n.children && n.children.length > 0;
        if (hasChildren) {
          return (
            <MenuGroup
              key={n.path}
              node={n}
              depth={depth}
              location={location}
              onClose={onClose}
              extLinkProps={extLinkProps}
            />
          );
        }
        return (
          <MenuLeaf
            key={n.path}
            node={n}
            depth={depth}
            location={location}
            onClose={onClose}
            extLinkProps={extLinkProps}
            badges={badges}
          />
        );
      })}
    </div>
  );
}
